// Step 1 range cards on the protein-snacks PDP. Cards are plain links; with JS
// a click is handed to the section's <product-stitching> so the product
// section is swapped in place. The element is re-rendered with the section,
// so listeners are bound per instance in connectedCallback.
//
// Page state on a client-side swap (range card or flavour tile) is also
// handled here, because the swapped section does not carry it:
// - document.title, from data-page-title rendered in the fetched section;
// - the breadcrumbs section, re-fetched for the new product.
// Step 1 itself comes from the fetched section as rendered for the new
// product: without atlas.snack_range it has no active card, Style line or
// nutrition pills, exactly as on a full page load.
// stitching:switched is dispatched from the old, already detached
// <product-stitching>, so it never reaches document. The swap is detected
// instead when the new <atlas-snack-range> connects while a switch that
// started with stitching:loading is pending.

// Guarded so a second execution (e.g. theme editor re-render) neither
// redefines the element nor adds the document listeners twice.
(() => {
    if (customElements.get("atlas-snack-range")) return;

    let pendingSwitch = null;

    function getBreadcrumbsWrapper() {
        return document.querySelector(".breadcrumbs-wrapper[data-section-id]");
    }

    function fetchBreadcrumbsSection(productHandle) {
        const wrapper = getBreadcrumbsWrapper();
        const sectionId = wrapper?.getAttribute("data-section-id");
        if (!sectionId) return Promise.resolve(null);

        const root = (window.Shopify?.routes?.root || "/").replace(/\/$/, "");
        const view = new URLSearchParams(window.location.search).get("view");
        const url =
            `${root}/products/${encodeURIComponent(productHandle)}` +
            `?section_id=${encodeURIComponent(sectionId)}` +
            (view ? `&view=${encodeURIComponent(view)}` : "");

        return fetch(url)
            .then((response) => (response.ok ? response.text() : null))
            .then((html) => {
                if (!html) return null;
                const doc = new DOMParser().parseFromString(html, "text/html");
                return {
                    sectionId,
                    section: doc.getElementById(`shopify-section-${sectionId}`)
                };
            })
            .catch((error) => {
                console.warn(
                    "AtlasSnackRange: Could not refresh breadcrumbs",
                    error
                );
                return null;
            });
    }

    function replaceBreadcrumbsSection(result) {
        if (!result?.section) return;

        const current = document.getElementById(
            `shopify-section-${result.sectionId}`
        );
        if (current) current.replaceWith(result.section);
    }

    document.addEventListener("stitching:loading", (event) => {
        const stitching = event.target;
        const section = stitching?.closest?.('[id^="shopify-section-"]');
        const snackRange = section?.querySelector("atlas-snack-range");
        if (!snackRange) return;

        const productHandle = event.detail?.productHandle;
        if (!productHandle) return;

        pendingSwitch = {
            sectionId: section.id,
            breadcrumbs: fetchBreadcrumbsSection(productHandle)
        };
    });

    document.addEventListener("stitching:error", () => {
        pendingSwitch = null;
    });

    class AtlasSnackRange extends HTMLElement {
        constructor() {
            super();
            this.handleClick = this.handleClick.bind(this);
        }

        connectedCallback() {
            this.addEventListener("click", this.handleClick);
            this.applyPendingSwitch();
        }

        disconnectedCallback() {
            this.removeEventListener("click", this.handleClick);
        }

        applyPendingSwitch() {
            const pending = pendingSwitch;
            if (!pending) return;

            const section = this.closest('[id^="shopify-section-"]');
            if (!section || section.id !== pending.sectionId) return;

            pendingSwitch = null;

            const pageTitle = this.getAttribute("data-page-title");
            if (pageTitle) document.title = pageTitle;

            pending.breadcrumbs.then(replaceBreadcrumbsSection);
        }

        getStitching() {
            const section = this.closest('[id^="shopify-section-"]');
            const stitching = section?.querySelector("product-stitching");

            return stitching && typeof stitching.switchToProduct === "function"
                ? stitching
                : null;
        }

        handleClick(event) {
            const card = event.target.closest(".atlas-snack-range-card");
            if (!card || !this.contains(card)) return;

            // Let modified clicks (new tab/window) behave like normal links.
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return;
            }

            if (card.classList.contains("is-active")) {
                event.preventDefault();
                return;
            }

            const stitching = this.getStitching();
            const productHandle = card.getAttribute("data-product-handle");
            if (!stitching || !productHandle) return;

            event.preventDefault();

            const variantId = card.getAttribute("data-variant-id") || null;

            card.classList.add("loading");
            card.setAttribute("aria-disabled", "true");

            stitching
                .switchToProduct(productHandle, variantId)
                .then((switched) => {
                    // On success this element has already been replaced with the new
                    // section; only the failure path needs to restore the card.
                    if (!card.isConnected) return;

                    card.classList.remove("loading");
                    card.removeAttribute("aria-disabled");

                    if (switched === false) {
                        window.location.href = card.href;
                    }
                });
        }
    }

    customElements.define("atlas-snack-range", AtlasSnackRange);
})();
